"use client";

import Link from "next/link";
import { Bookmark, Check, ArrowUpRight, Zap, Star } from "lucide-react";
import { cn } from "@/lib/utils";
import { usePinnedTools } from "@/lib/personalize";

export type ToolCardVariant = "default" | "compact" | "featured" | "related" | "home";
export type ToolCardSize = "sm" | "md" | "lg";

export interface ToolData {
  id: string;
  name: string;
  description: string;
  category: string;
  slug: string;
  popularity: number;
  featured?: boolean;
  trending?: boolean;
  new?: boolean;
  icon?: string;
  features?: string[];
  customUrl?: string;
}

interface ToolCardProps {
  tool: ToolData;
  variant?: ToolCardVariant;
  size?: ToolCardSize;
  className?: string;
  showPopularity?: boolean;
  showCategory?: boolean;
  showPin?: boolean;
  onClick?: () => void;
}

const SIZE: Record<ToolCardSize, { padding: string; title: string; desc: string }> = {
  sm: { padding: "p-4", title: "text-[15px]", desc: "text-[13px]" },
  md: { padding: "p-5", title: "text-lg", desc: "text-sm" },
  lg: { padding: "p-6", title: "text-xl", desc: "text-[15px]" },
};

export function ToolCard({
  tool,
  variant = "default",
  size = "md",
  className,
  showPopularity = true,
  showCategory = true,
  showPin = true,
  onClick,
}: ToolCardProps) {
  const sizes = SIZE[size];
  const isFeatured = variant === "featured";
  const { isPinned, toggle } = usePinnedTools();
  const pinned = isPinned(tool.slug);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      e.preventDefault();
      onClick();
    }
  };

  const handlePin = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(tool.slug);
  };

  const href = tool.customUrl ?? `/tools/${tool.slug}`;

  return (
    <Link
      href={href}
      onClick={handleClick}
      className={cn(
        "group relative flex min-w-0 w-full flex-col overflow-hidden rounded-xl border transition-[border-color,box-shadow,transform] duration-150 ease-out hover:-translate-y-0.5",
        isFeatured
          ? "border-transparent bg-[var(--color-accent-strong)] shadow-[var(--shadow-md)] hover:shadow-[var(--shadow-lg)]"
          : "border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-xs)] hover:border-[var(--color-accent)] hover:shadow-[var(--shadow-md)]",
        sizes.padding,
        className
      )}
    >
      <span
        className={cn(
          "pointer-events-none absolute inset-x-0 top-0 h-0.5 origin-left scale-x-0 transition-transform duration-200 ease-out group-hover:scale-x-100",
          isFeatured ? "bg-[color-mix(in_oklab,var(--color-accent-fg)_45%,transparent)]" : "bg-[var(--color-accent)]"
        )}
        aria-hidden="true"
      />
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-1.5">
          {showCategory && (
            <span
              className={cn(
                "shrink-0 rounded-md px-2 py-0.5 text-[11px] font-semibold",
                isFeatured
                  ? "bg-[color-mix(in_oklab,var(--color-accent-fg)_18%,transparent)] text-[var(--color-accent-fg)]"
                  : "bg-[var(--color-surface-2)] text-[var(--color-text-muted)]"
              )}
            >
              {tool.category}
            </span>
          )}
          {tool.new && (
            <span className="rounded-md bg-[var(--color-signal-100)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-signal-700)]">
              New
            </span>
          )}
          {tool.trending && (
            <span className="flex items-center gap-1 rounded-md bg-[var(--color-accent-soft)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-accent-strong)]">
              <Zap className="h-3 w-3" aria-hidden="true" />
              Trending
            </span>
          )}
          {tool.featured && !isFeatured && (
            <span className="flex items-center gap-1 rounded-md border border-[var(--color-accent)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-accent-strong)]">
              <Star className="h-3 w-3" aria-hidden="true" />
              Featured
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {showPin && (
            <button
              type="button"
              onClick={handlePin}
              aria-label={pinned ? `Unpin ${tool.name} from your favorites` : `Pin ${tool.name} to your favorites`}
              aria-pressed={pinned}
              className={cn(
                "inline-flex h-7 w-7 items-center justify-center rounded-md border transition-colors",
                pinned
                  ? "border-[var(--color-signal)] bg-[var(--color-signal-100)] text-[var(--color-signal-700)]"
                  : isFeatured
                    ? "border-[color-mix(in_oklab,var(--color-accent-fg)_25%,transparent)] bg-[color-mix(in_oklab,var(--color-accent-fg)_10%,transparent)] text-[color-mix(in_oklab,var(--color-accent-fg)_80%,transparent)] hover:bg-[color-mix(in_oklab,var(--color-accent-fg)_20%,transparent)]"
                    : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-subtle)] opacity-0 hover:border-[var(--color-accent)] hover:text-[var(--color-accent-strong)] group-hover:opacity-100 focus-visible:opacity-100"
              )}
            >
              {pinned ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Bookmark className="h-3.5 w-3.5" aria-hidden="true" />}
            </button>
          )}
          {showPopularity && tool.popularity >= 90 && !isFeatured && (
            <span className="shrink-0 rounded-md bg-[var(--color-accent-strong)] px-2 py-0.5 text-[11px] font-semibold text-[var(--color-accent-fg)]">
              Popular
            </span>
          )}
        </div>
      </div>

      <span className="mt-3 flex items-start justify-between gap-2">
        <h3
          className={cn(
            "font-[family-name:var(--font-display)] font-semibold tracking-[-0.01em] text-balance",
            sizes.title,
            isFeatured ? "text-[var(--color-accent-fg)]" : "text-[var(--color-text)]"
          )}
        >
          {tool.name}
        </h3>
        <ArrowUpRight
          className={cn(
            "mt-1 h-5 w-5 shrink-0 transition-[color,transform] duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5",
            isFeatured ? "text-[color-mix(in_oklab,var(--color-accent-fg)_65%,transparent)]" : "text-[var(--color-text-subtle)] group-hover:text-[var(--color-accent-strong)]"
          )}
          aria-hidden="true"
        />
      </span>

      <p
        className={cn(
          "mt-1.5 line-clamp-2 leading-relaxed",
          sizes.desc,
            isFeatured ? "text-[color-mix(in_oklab,var(--color-accent-fg)_80%,transparent)]" : "text-[var(--color-text-muted)]"
        )}
      >
        {tool.description}
      </p>

      {tool.features && tool.features.length > 0 && size !== "sm" && !isFeatured && (
        <ul className="mt-4 flex flex-wrap gap-1.5" aria-label={`${tool.name} features`}>
          {tool.features.slice(0, variant === "home" ? 4 : 3).map((feature) => (
            <li
              key={feature}
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text-muted)]"
            >
              {feature}
            </li>
          ))}
          {tool.features.length > (variant === "home" ? 4 : 3) && (
            <li className="rounded-md border border-dashed border-[var(--color-border-strong)] px-2 py-0.5 text-[11px] font-medium text-[var(--color-text-subtle)]">
              +{tool.features.length - (variant === "home" ? 4 : 3)} more
            </li>
          )}
        </ul>
      )}

      {isFeatured && (
        <div className="mt-4 flex items-center justify-between border-t border-[color-mix(in_oklab,var(--color-accent-fg)_20%,transparent)] pt-4">
          <span className="text-xs text-[color-mix(in_oklab,var(--color-accent-fg)_70%,transparent)]">Part of DevStackIO platform</span>
          <span className="rounded-md bg-[var(--color-signal)] px-2.5 py-1 text-xs font-bold text-[var(--color-signal-ink)]">
            Explore tool
          </span>
        </div>
      )}
    </Link>
  );
}
